import { useCallback, useEffect, useState, createRef } from 'react'
import { useEffectOnce } from 'usehooks-ts'
import styled from 'styled-components'
import headerImage from './images/header.png'
import headerVideo from './video/header.mp4'
import headerVideoWebm from './video/header.webm'
import headerVideoMobile from './video/header-mobile.mp4'
import headerVideoWebmMobile from './video/header-mobile.webm'
import headerVideoFR from './video/header-fr.mp4'
import headerVideoWebmFR from './video/header-fr.webm'
import headerVideoMobileFR from './video/header-mobile-fr.mp4'
import headerVideoWebmMobileFR from './video/header-mobile-fr.webm'
import Authors from 'components/Authors'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'

const Header = ({
  tenant,
  colorMode,
  titleText,
  leadText,
  caption,
  authors,
  publishDate,
}: {
  tenant?: Tenant
  colorMode?: string
  titleText?: string
  leadText?: string
  caption?: string
  authors?: any
  publishDate?: string
}) => {
  const [hasHeader, setHasHeader] = useState(false)
  const [isMobile, setIsMobile] = useState(true)
  const [isWide, setIsWide] = useState(false)

  const handleResize = (): void => {
    setIsMobile(window.innerWidth < 711)
    setIsWide(
      window.innerWidth >= 1360 || window.innerWidth > window.innerHeight,
    )
  }

  useEffectOnce(() => {
    const headerElement = document.querySelector('[class*=FullHeader_root]')
    if (headerElement) {
      setHasHeader(true)
    }
    // set height when window is resized
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  return (
    <HeaderWrapper>
      <TopWrapper>
        {headerVideo && (
          <div className='header-image'>
            <video playsInline autoPlay loop muted>
              <source
                src={
                  isMobile
                    ? tenant?.lang === 'fr'
                      ? headerVideoFR
                      : headerVideoWebmMobile
                    : tenant?.lang === 'fr'
                      ? headerVideoWebmFR
                      : headerVideoWebm
                }
                type='video/webm'
              />
              <source
                src={
                  isMobile
                    ? tenant?.lang === 'fr'
                      ? headerVideoMobileFR
                      : headerVideoMobile
                    : tenant?.lang === 'fr'
                      ? headerVideoFR
                      : headerVideo
                }
                type='video/mp4'
              />
            </video>
          </div>
        )}
        {!headerVideo && (
          <div className='header-image'>
            <img src={headerImage} alt='header' />
          </div>
        )}
        {caption && <Caption>{caption}</Caption>}
      </TopWrapper>
      <div className='title-wrapper'>
        <div className='titles'>
          <h1
            dangerouslySetInnerHTML={{
              __html: titleText ? titleText : 'titleText fehlt',
            }}
          ></h1>
        </div>
        <h2>{leadText}</h2>

        <div className='authors'>
          {authors && authors.length > 0 && (
            <Authors
              authors={authors}
              tenant={
                tenant
                  ? tenant
                  : {
                      key: 'tagesanzeiger',
                      name: 'Tages-Anzeiger',
                      domain: 'tagesanzeiger',
                      feedId: 3,
                      lang: 'de',
                    }
              }
            />
          )}
          {publishDate && <div className='publication'>{publishDate}</div>}
        </div>
      </div>
    </HeaderWrapper>
  )
}

const HeaderWrapper = styled.div`
  .publication {
    font-size: 1.2rem;
    color: var(--gray-3);
    margin-bottom: 6rem;
    /* @media only screen and (max-width: 712px) {
    margin-bottom: 4rem;
    } */
    text-align: center;
    z-index: 10;
    position: relative;
  }
  .title-wrapper {
    z-index: 2;
    /* top: -60px; */
    width: 668px;
    margin: 0 auto;
    @media only screen and (max-width: 768px) {
      max-width: 90%;
      top: 0;
    }
    position: relative;
    .titles {
      width: 100%;
      z-index: 99;
      position: relative;
      h1 {
        position: relative;
        margin: 0 auto;
        font-family: var(--font-sole);
        font-weight: 600;
        color: var(--text-color);
        text-align: center;
        font-size: 3.3rem;
        line-height: 3.8rem;
        -webkit-font-smoothing: antialiased;
        @media only screen and (min-width: 768px) {
          font-size: 4rem;
          line-height: 5rem;
          max-width: 75%;
          _color: yellow;
        }
        @media only screen and (min-width: 1200px) {
          font-size: 5rem;
          line-height: 6rem;
          _color: orange;
          _text-shadow:
            1px 1px 40px #000,
            1px 1px 40px #000;
        }
        @media (orientation: landscape) and (max-height: 1000px) {
          _color: green;
          font-size: 5rem;
          line-height: 6rem;
        }
      }
    }
  }

  h2 {
    font-family: var(--font-sans-serif);
    color: var(--text-color);
    font-weight: 400;
    font-size: 1.8rem;
    line-height: 2.7rem;
  }
`

const Caption = styled.div`
  max-width: 1072px;
  margin: 0 auto;
  font-size: 1.2rem;
  color: #7d7d7d; // var(--meta-data);
  display: grid;
  position: relative;
  @media only screen and (max-width: 768px) {
    padding-left: 1rem;
    top: -1rem;
    text-align: right;
  }
  top: -2rem;
  text-align: left;
  padding-left: 5rem;
  padding-right: 2rem;
  grid-column: 1;
`

const TopWrapper = styled.div`
  background-color: transparent;
  overflow: hidden;
  position: relative;
  max-width: 1072px;
  @media only screen and (max-width: 1023px) {
    max-width: 832px;
  }
  margin: 0 auto;

  .header-image {
    background-color: var(--site-background);
    position: relative;
    text-align: center;
    img,
    video {
      width: 95%;
      max-width: 1072px;
      text-align: center;
    }

    /*
    height: 50vh;
    min-height: 90%;
    @media (orientation: landscape) {
      height: 50vh;
      min-height: 600px;
    }
    @media only screen and (max-width: 768px) {
      height: 40vh;
    }
    @media only screen and (max-width: 600px) {
      height: 23vh;
    }
    @media only screen and (max-width: 400px) {
      height: 30vh;
    }
    img,
    video,
    div {
      width: 100%;
      height: 100%;
      position: absolute;
      object-fit: cover;
      overflow: visible;
      z-index: 1;
    }
    */
  }
`

export default Header
